import { FC, useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from "@capacit-com/lara-law-shared-ui";
import { Home, Folder, ChevronRight, ChevronDown } from "lucide-react";
import Workspace, { WorkspaceFolder } from "@/models/workspace/Workspace";
import {
  Item,
  ItemType,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LABELS,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { cn } from "@/lib/utils";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { findFolderById } from "@/pages/workspace/utils/folderUtils";

export type MoveItemModalProps = {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace | null;
  item: Item | null;
  onMove: (params: {
    targetFolderId: string | null;
    itemType: ItemType;
    itemId: string;
  }) => Promise<void>;
  isPending: boolean;
};

/** Collect all descendant folder ids for a folder (including itself) */
function getFolderAndDescendantIds(
  folder: WorkspaceFolder,
  acc: Set<string> = new Set()
): Set<string> {
  acc.add(folder.id);
  folder.folders.forEach((f) => getFolderAndDescendantIds(f, acc));
  return acc;
}

const MoveItemModal: FC<MoveItemModalProps> = ({
  isOpen,
  onClose,
  workspace,
  item,
  onMove,
  isPending,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | "root">(
    "root"
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const disabledFolderIds = useMemo(() => {
    if (!item || item.type !== ITEM_TYPE_FOLDER || !workspace)
      return new Set<string>();
    const folder = findFolderById(workspace, item.id);
    return folder ? getFolderAndDescendantIds(folder) : new Set();
  }, [item, workspace]);

  const toggleExpanded = (folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!item) return;
    const targetId = selectedFolderId === "root" ? null : selectedFolderId;
    await onMove({
      targetFolderId: targetId,
      itemType: item.type,
      itemId: item.id,
    });
    onClose();
  };

  const renderFolderRow = (
    folder: WorkspaceFolder,
    depth: number
  ): React.ReactNode => {
    const isDisabled = disabledFolderIds.has(folder.id);
    const hasChildren = folder.folders.length > 0;
    const isExpanded = expandedIds.has(folder.id);
    const isSelected = selectedFolderId === folder.id;

    return (
      <div key={folder.id} className="flex flex-col">
        <div
          className={cn(
            "flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer text-sm",
            isDisabled && "opacity-50 cursor-not-allowed",
            !isDisabled && "hover:bg-muted",
            isSelected && !isDisabled && "bg-muted ring-1 ring-primary/50"
          )}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
          onClick={() => {
            if (isDisabled) return;
            setSelectedFolderId(folder.id);
            if (hasChildren) toggleExpanded(folder.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (isDisabled) return;
              setSelectedFolderId(folder.id);
              if (hasChildren) toggleExpanded(folder.id);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Vælg mappe ${folder.name}`}
        >
          {hasChildren ? (
            <button
              type="button"
              className="shrink-0 p-0.5 rounded hover:bg-muted-foreground/10"
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) toggleExpanded(folder.id);
              }}
              aria-label={isExpanded ? "Luk mappe" : "Åbn mappe"}
            >
              {isExpanded ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          <Folder className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{folder.name}</span>
        </div>
        {hasChildren && isExpanded && (
          <div>{folder.folders.map((f) => renderFolderRow(f, depth + 1))}</div>
        )}
      </div>
    );
  };

  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-xl">
            Flyt {ITEM_TYPE_LABELS[item.type].indefinite}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Vælg destination for &quot;{item.name}&quot;
          </DialogDescription>
        </DialogHeader>

        <div
          className="border rounded-md overflow-auto min-h-[200px] max-h-[320px] bg-muted/30 py-1"
          role="tree"
          aria-label="Mappestruktur"
        >
          <div
            className={cn(
              "flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer text-sm hover:bg-muted",
              selectedFolderId === "root" && "bg-muted ring-1 ring-primary/50"
            )}
            onClick={() => setSelectedFolderId("root")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSelectedFolderId("root");
              }
            }}
            role="treeitem"
            tabIndex={0}
            aria-label="Vælg Hjem (roden)"
          >
            <Home className="size-4 shrink-0 text-muted-foreground" />
            <span>Hjem</span>
          </div>
          {workspace?.folders.map((folder) => renderFolderRow(folder, 0))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuller
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isPending}
            className="min-w-[100px]"
          >
            {isPending ? (
              <Spinner className="size-4 text-primary-foreground" />
            ) : (
              "Flyt"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveItemModal;
